import template from 'msp-scoring/template.json';
import { useEffect, useRef, useState } from 'react';
import { Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Banner } from 'banner/Banner';
import { SessionManager } from 'session-manager';
import { getCookie } from 'utilities/utilities';

import { Tool } from '@client:common/Tool';
import { Template } from '@client:common/Template';
import { Model } from '@client:common/Model';
import { AuthenticatedAccount } from '@client:common/AuthenticatedAccount';
import { LinkButton } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { GlobalStore } from '@client:common/global-store';
import { Icon } from '@client:common/icon';

import { LandingPage } from './landing-page';
import { ModelComparator } from './model-comparator';
import { SelectionWizard } from './selection-wizard';
import { AccountManager } from './account-manager';
import { AccountPage } from './account-page/AccoutPage';
import { SessionHistory } from './session-history/SessionHistory';
import { FeedbackModal } from "./feedback-modal/FeedbackModal";
import { AssistantFrame, AssistantFrameRef } from './ai-assistant/views/assistant-frame/AssistantFrame';
import ReactGA from "react-ga4";
import './App.dark.scss';
import './App.light.scss';

const GA_MEASUREMENT_ID = "G-YV0069K3YY";

ReactGA.initialize(GA_MEASUREMENT_ID);

export function App() {
  const [models, setModels] = useState<Model[]>([]);
  const [newModels, setNewModels] = useState<Model[]>([]);
  const [tools, setTools] = useState<{ [toolName: string]: Tool }>(null);
  // const [template, setTemplate] = useState();
  const [loadSession, setLoadSession] = useState();
  const [
    modelComparisonTableRowSpecifications,
    setModelComparisonTableRowSpecifications
  ] = useState<Array<{ path: string }>>([]);
  const [authenticatedAccount, setAuthenticatedAccount] =
    useState<AuthenticatedAccount>(null);

  const [isAssistantFullScreen, setIsAssistantFullScreen] = useState(false);
  const assistantFrameRef = useRef<AssistantFrameRef>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);

  const updateIsMobile = () => {
    const screenWidth = window.innerWidth;
    const updatedIsMobile = screenWidth < 640;
    setIsMobile(updatedIsMobile);
  };

  const SURVEY_REMINDER_COOKIE_NAME = "hideSurveyOnStart";

  useEffect(() => {
    const surveyCookieValue = getCookie(SURVEY_REMINDER_COOKIE_NAME);
    if (surveyCookieValue !== true) {
      setShowSurveyModal(true);
    }
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  useEffect(() => {
    fetch('/data/models.json')
      .then<{
        models: Model[];
        modelTableRowSpecifications: Array<{ path: string }>;
      }>((response) => response.json())
      .then((payload) => {
        payload.models = payload.models.filter((model) => model.visible);
        for (const model of payload.models) {
          const id = model.acronym ? model.acronym : model.name;
          model.id = id
            .toLocaleLowerCase()
            .replace(/[^\w\d]/g, '-')
            .replace(/(?:^-|-$)/g, '');
        }
        setModels(payload.models);
        setModelComparisonTableRowSpecifications(
          payload.modelTableRowSpecifications
        );
      });
  }, []);

  useEffect(() => {
    fetch('/data/newModels.json')
      .then<{
        models: Model[];
        modelTableRowSpecifications: Array<{ path: string }>;
      }>((response) => response.json())
      .then((payload) => {
        payload.models = payload.models.filter((model) => model.visible);
        for (const model of payload.models) {
          const id = model.acronym ? model.acronym : model.name;
          model.id = id
            .toLocaleLowerCase()
            .replace(/[^\w\d]/g, '-')
            .replace(/(?:^-|-$)/g, '');
        }
        setNewModels(payload.models);
      });
  }, []);

  useEffect(() => {
    const fetchToolFile = async (tool: {
      displayName: string;
      filename: string;
    }) => {
      const module = await import(`msp-scoring/tools/${tool.filename}`);
      return {
        [tool.displayName]: module.default
      } as { [toolName: string]: Tool };
    };

    fetch('/data/flow-chart/tools.json')
      .then<{ tools: Array<{ displayName: string; filename: string }> }>(
        (response) => response.json()
      )
      .then((payload) => Promise.all(payload.tools.map(fetchToolFile)))
      .then((payload) =>
        setTools(
          payload.reduce(
            (accumulator, tool) => Object.assign(accumulator, tool),
            {}
          )
        )
      );
  }, []);

  useEffect(() => {
    const globalStore = GlobalStore.getInstance();
    const subscription = globalStore.select('authenticatedAccount').subscribe({
      next: setAuthenticatedAccount
    });
    const assistantSubscription = globalStore.select('isAssistantFullScreen').subscribe({
      next: setIsAssistantFullScreen
    });

    return () => {
      subscription.unsubscribe();
      assistantSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isAssistantFullScreen) {
      document.documentElement.classList.add('overflow-hidden');
    } else {
      document.documentElement.classList.remove('overflow-hidden');
    }
    return () => document.documentElement.classList.remove('overflow-hidden');
  }, [isAssistantFullScreen]);
  return (
    <div>
      <div className={`app-content ${isAssistantFullScreen ? 'app-content--assistant-fullscreen' : ''}`}>
        <Routes>
          <Route
            path='/'
            element={
              <>
                <Banner setLoadSession={setLoadSession}>
                  {authenticatedAccount && (
                    <LinkButton
                      className='session-manager__action open-session-page'
                      to='/pages/session-history'
                    >
                      <Ripple />
                      <Icon icon='history' />
                      {!isMobile && (
                        <span className='session-manager__action-text'>
                          VIEW SESSION HISTORY
                        </span>
                      )}
                    </LinkButton>
                  )}
                  {authenticatedAccount?.isVerified && (
                    <LinkButton
                      to='/pages/account/profile'
                      className='app__go-to-profile-page'
                    >
                      <Ripple />
                      <Icon icon='account_circle' />
                      {!isMobile ? authenticatedAccount.firstName +
                        ' ' +
                        authenticatedAccount.lastName : ''}
                    </LinkButton>
                  )}
                  <SessionManager />
                </Banner>
                <Outlet />
                {showSurveyModal && (
                  <div
                    className="modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FeedbackModal
                      isAssistantPresent
                      feedbackCookieName={SURVEY_REMINDER_COOKIE_NAME}
                      closeModal={() => setShowSurveyModal(false)} />
                  </div>
                )}
              </>
            }
          >
            <Route
              path='/'
              element={<LandingPage models={newModels} />} />

            <Route path='pages/*'>
              <Route
                path='model-comparator'
                element={
                  <ModelComparator
                    models={models}
                    tools={tools}
                    template={template as unknown as Template}
                    rowSpecifications={modelComparisonTableRowSpecifications}
                  />
                }
              />

              <Route
                path='selection-wizard'
                element={
                  <SelectionWizard
                    models={models}
                    setModels={setModels}
                    tools={tools}
                    setTools={setTools}
                    template={template as unknown as Template}
                    loadSession={loadSession}
                    isMobile={window.innerWidth < 1024} // Use a larger breakpoint for selection wizard
                  // setTemplate={setTemplate}
                  />
                }
              />

              <Route
                path='session-history'
                element={<SessionHistory setLoadSession={setLoadSession} />}
              />
              <Route
                path='account/*'
                element={<AccountManager />} />
              <Route
                path='account/profile'
                element={<AccountPage setShowSurveyModal={setShowSurveyModal} />} />
            </Route>
          </Route>

        </Routes>
      </div>
      {isAssistantFullScreen &&
        <div
          className='app__assistant-backdrop'
          onClick={() => assistantFrameRef.current?.close()}
        />
      }
      <AssistantFrame ref={assistantFrameRef} />
    </div>
  );
}
